import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useMessages = (user, token, showMessage) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [readMessages, setReadMessages] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Fetch all direct messages
  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/messages/');
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Fetch messages error:', error);
    }
  }, [token]);

  // Select user to chat with
  const handleSelectChatUser = (chatUser) => {
    setSelectedChatUser(chatUser);
    if (chatUser) {
      setReadMessages(prev => ({
        ...prev,
        [chatUser.id]: new Date().toISOString(),
      }));
    }
  };

  // Send direct message
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!selectedChatUser || (!newMessage.trim() && !selectedFile)) return;

    setIsLoadingMessages(true);
    try {
      let mediaUrl = '';
      if (selectedFile) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadRes = await apiClient('/api/messages/upload', {
          method: 'POST',
          isFormData: true,
          body: formData,
        });

        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          mediaUrl = uploadData.media_url;
        }
        setIsUploading(false);
      }

      const response = await apiClient('/api/messages/', {
        method: 'POST',
        body: {
          recipient_id: selectedChatUser.id,
          content: newMessage.trim(),
          media_url: mediaUrl || undefined,
        },
      });

      if (response.ok) {
        await fetchMessages();
        setNewMessage('');
        setSelectedFile(null);
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to send message', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error sending message', 'error');
    } finally {
      setIsLoadingMessages(false);
      setIsUploading(false);
    }
  };

  // Get conversation history between current user and target user
  const getConversationMessages = (targetUserId) => {
    if (!user || !targetUserId) return [];
    return messages.filter(
      (m) =>
        (m.sender_id === user.id && m.recipient_id === targetUserId) ||
        (m.sender_id === targetUserId && m.recipient_id === user.id)
    );
  };

  // Get latest message between current user and target user
  const getLastMessage = (targetUserId) => {
    const conv = getConversationMessages(targetUserId);
    return conv.length > 0 ? conv[conv.length - 1] : null;
  };

  return {
    messages,
    newMessage,
    setNewMessage,
    selectedChatUser,
    setSelectedChatUser,
    readMessages,
    selectedFile,
    setSelectedFile,
    isUploading,
    isLoadingMessages,
    fetchMessages,
    handleSelectChatUser,
    handleSendMessage,
    getConversationMessages,
    getLastMessage,
  };
};
